// ABOUTME: Runs the notificationCreated/notificationRead subscriptions and keeps the Apollo cache
// ABOUTME: live: prepends new notifications, bumps the count, and syncs read state across tabs.

import { useApolloClient } from '@apollo/client';
import { useAuth } from '@src/hooks';
import { ReactNode } from 'react';
import { useIntl } from 'react-intl';

import {
    NotificationsDocument,
    NotificationsQuery,
    UnreadNotificationCountDocument,
    UnreadNotificationCountQuery,
    useNotificationCreatedSubscription,
    useNotificationReadSubscription,
} from '../graphql/notifications.generated';

const NotificationSubscriptionContextProvider = ({ children }: { children: ReactNode }) => {
    const { sessionData } = useAuth();
    const client = useApolloClient();
    // The subscriber's locale resolves the pushed title/message; it must also key the cache writes
    // below so they target the same entries the useNotifications query populates (variables include
    // language).
    const { locale } = useIntl();

    const company = sessionData?.attributes?.company ?? '';
    const userId = sessionData?.username ?? '';
    // dealerships are not declared on the session type but may be present at runtime; read them
    // defensively so dealership-scoped users still receive their dealership notifications live.
    const dealerships = (sessionData?.attributes as { dealerships?: string[] } | undefined)
        ?.dealerships;
    const roles = sessionData?.roles;

    useNotificationCreatedSubscription({
        shouldResubscribe: true,
        skip: !company || !userId,
        variables: { company, userId, dealerships, roles, locale },
        onData: ({ data }) => {
            const notification = data.data?.notificationCreated;
            if (!notification) return;

            // A fresh notification is unread, so it belongs in both list variants
            // (unread-only and "Show all"); update whichever entries are in the cache.
            [true, false].forEach(onlyUnread => {
                client.cache.updateQuery<NotificationsQuery>(
                    { query: NotificationsDocument, variables: { onlyUnread, language: locale } },
                    existing => {
                        if (!existing) return existing;
                        if (existing.notifications.some(n => n.id === notification.id)) {
                            return existing;
                        }
                        return { notifications: [notification, ...existing.notifications] };
                    },
                );
            });

            client.cache.updateQuery<UnreadNotificationCountQuery>(
                { query: UnreadNotificationCountDocument },
                existing =>
                    existing
                        ? { unreadNotificationCount: existing.unreadNotificationCount + 1 }
                        : existing,
            );
        },
    });

    // Read-state sync: when this user marks notifications as read in any tab/device, refetch the
    // active list/count queries so every other session drops them too. Refetching (rather than
    // cache surgery) keeps the server as the single source of truth for both list variants and the
    // badge; the event is strictly personal (the server only forwards the subscriber's own reads).
    useNotificationReadSubscription({
        shouldResubscribe: true,
        skip: !userId,
        variables: { userId },
        onData: ({ data }) => {
            if (!data.data?.notificationRead) return;
            client.refetchQueries({ include: ['Notifications', 'UnreadNotificationCount'] });
        },
    });

    // This provider only drives the live subscriptions; it renders its children unchanged.
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
};

export default NotificationSubscriptionContextProvider;
